import { IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { MouseEventHandler, useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';
import { appActions } from 'app/app.actions';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

export const ConversationUnlinkButton = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { conversation } = useAppSelector((state) => ({
    conversation: state.conversation.metaConversation.conversation,
  }));

  const [updateConversationUnlink, { isLoading }] =
    conversationsApi.useUpdateConversationUnlinkMutation();

  const handleUnlink = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async (e) => {
      e.stopPropagation();

      const { isConfirmed, confirm } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: 'Are you sure?',
              description: `Are you sure you want to unlink this conversation from #${conversation.linked_to} ?`,
              confirm: {
                label: 'Unlink',
                async: true,
                asyncLabel: 'Unlink in progress...',
              },
            },
          },
        }),
      );
      if (isConfirmed) {
        confirm(async () => {
          const csid = conversation.id!;
          const response = await updateConversationUnlink({ csid });
          if (!isResponseWithData(response)) {
            notify(
              {
                title: 'Error during conversation unlink.',
              },
              NotificationType.Error,
            );
            return;
          }

          pubSub.publish('conversationUnlinked', { csid });
          dispatch(
            conversationActions.conversationGet({
              csid,
              skipStoreAndWs: true,
            }),
          );
          notify(
            {
              title: 'Conversation unlinked',
              text: `"Conversation #${csid}" has been unlinked.`,
              icon: 'i-done',
              time: 3000,
            },
            NotificationType.Success,
          );
        });
      }
    },
    [
      conversation.id,
      dispatch,
      notify,
      conversation.linked_to,
      updateConversationUnlink,
    ],
  );

  if (!conversation.linked_to || !conversation.id) {
    return null;
  }

  return (
    <Tooltip
      content={`Remove link to a parent conversation #${conversation.linked_to}`}
    >
      <IconButton
        icon="link-slash"
        variant="secondary"
        isLoading={isLoading}
        onClick={handleUnlink}
      />
    </Tooltip>
  );
};
